import {
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import CongratulationMessage from "./CongratulationMessage";
import WebsiteImageOption from "./WebsiteImageOption";
import { useEffect, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { websiteActionEndPointLogic } from "../../../../../../logic/AssetManagement/Websites/websiteActionEndPointLogic";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../../../../features/userSlice";
import CircularProgress from "@mui/material/CircularProgress";

interface CreateWebsiteModalProps {
  open: boolean;
  handleClose: () => void;
  setWebsiteName: (value: string) => void;
  websiteName: string;
  setWebsiteType: any;
  websiteType: any;
  industryList: any;
  geoList: any;
  regionList: any;
  setSiteTitle: any;
  siteTitle: any;
  userMail: any;
  setReloadTable: any;
}

function CreateNewWebsiteModal({
  open,
  handleClose,
  websiteName,
  setWebsiteName,
  setWebsiteType,
  websiteType,
  industryList,
  geoList,
  regionList,
  setSiteTitle,
  siteTitle,
  userMail,
  setReloadTable,
}: CreateWebsiteModalProps) {
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(false);
  const [errorWPUserName, setErrorWPUserName] = useState(false);
  const [region, setRegion] = useState("");
  const [geo, setGeo] = useState("");
  const [industry, setIndustry] = useState("");
  const [wpUsername, setWpUsername] = useState("");
  const [wpPassword, setWpPassword] = useState("");
  const [createSite, setCreateSite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCongratulationMessage, setShowCongratulationMessage] =
    useState(false);
  const [congratulationMessageOpen, setCongratulationMessageOpen] =
    useState(false);

  const handleCongratulationMessageClose = () => {
    setShowCongratulationMessage(false);
    setCongratulationMessageOpen(false);
    setReloadTable(true);
  };

  const websitelisting = [{ label: "PPC" }, { label: "SEO" }];

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // const handleMouseUpPassword = (
  //   event: React.MouseEvent<HTMLButtonElement>
  // ) => {
  //   event.preventDefault();
  // };

  const data = {
    ui_site_name: websiteName,
    region: region,
    site_type: websiteType,
    site_title: siteTitle,
    geo_id: geo,
    industry_id: industry,
    site_owner_email: userMail,
    site_owner_password: wpPassword,
    site_owner_username: wpUsername,
  };

  const handleCancel = () => {
    handleClose();
    setWebsiteName("");
    setWebsiteType("");
    setSiteTitle("");
    setRegion("");
    setGeo("");
    setIndustry("");
    setWpUsername("");
    setWpPassword("");
    setError(false);
    setErrorWPUserName(false);
  };

  const validateData = (data: any) => {
    for (const key in data) {
      if (data[key] === "" || data[key] === undefined || data[key] === null) {
        console.error(`Validation failed: ${key} is invalid.`);
        return false; // Return false or handle validation failure
      }
    }
    return true; // Return true if all values are valid
  };

  const isAllFileldsValid = validateData(data);

  useEffect(() => {
    if (createSite === true && isAllFileldsValid) {
      setIsLoading(true); // Set loading to true when API request starts
      websiteActionEndPointLogic
        .postWebsite(oauthTokenHeader, data, setShowCongratulationMessage)
        .then(() => {
          setCreateSite(false); // Perform actions after successful request
          setIsLoading(false); // Set loading to false after request is completed
          // setShowCongratulationMessage(true);
          setCongratulationMessageOpen(true);
        })
        .catch((error) => {
          setIsLoading(false); // Set loading to false in case of an error
          console.error("Error:", error);
        });
    }
    if (showCongratulationMessage === true) {
      handleCancel();
    }
    // eslint-disable-next-line
  }, [createSite === true, isAllFileldsValid, oauthTokenHeader, data]);

  const handlePasswordChange = (e: any) => {
    const value = e.target.value;
    setWpPassword(value);
    setError(value.length > 0 && value.length < 8); // Error if length < 8 and input is not empty
  };

  const handleEmailChange = (e: any) => {
    const value = e.target.value;
    setWpUsername(value);

    // Validate email with a regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setErrorWPUserName(value.length > 0 && !emailRegex.test(value)); // Error if input is not empty and not a valid email
  };

  return (
    <>
      {showCongratulationMessage ? (
        <Dialog
          open={congratulationMessageOpen}
          onClose={handleCongratulationMessageClose}
          maxWidth="md"
          fullWidth
        >
          <Paper>
            <CongratulationMessage
              handleCongratulationMessageClose={
                handleCongratulationMessageClose
              }
            />
          </Paper>
        </Dialog>
      ) : (
        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
        >
          <Paper sx={{ padding: "16px 24px 20px 24px" }}>
            <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
              Create a New Website
            </DialogTitle>
            <Box>
              <Typography sx={{ mb: 2, fontSize: 16, fontWeight: 400 }}>
                Details
              </Typography>
              <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="outlined-multiline-flexible"
                    label="Website Name"
                    placeholder="How you like the site to be called"
                    required
                    fullWidth
                    color="secondary"
                    size="small"
                    value={websiteName}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      // Allow only numbers and alphabetic characters, and limit to 8 characters
                      if (/^[a-zA-Z0-9 ]*$/.test(inputValue)) {
                        setWebsiteName(inputValue);
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Minimum of 8 characters, only letters and numbers"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={websitelisting.map(
                      (websitename) => websitename.label
                    )}
                    onChange={(_, newValue) => setWebsiteType(newValue || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Website type"
                        color="secondary"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder="Choose site type from the list"
                        required
                      />
                    )}
                    size="small"
                  />
                </Grid>
              </Grid>
              <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={industryList?.payload}
                    getOptionLabel={(option: any) => option?.industry_name}
                    onChange={(_, newValue: any) =>
                      setIndustry(newValue?.shared_uid)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Industry"
                        color="secondary"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder="Choose industry from the list"
                        required
                      />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={geoList}
                    getOptionLabel={(option: any) => option?.name}
                    onChange={(_, newValue: any) =>
                      setGeo(newValue?.shared_uid)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Geography"
                        color="secondary"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder="Choose geography from the list"
                        required
                      />
                    )}
                    size="small"
                  />
                </Grid>
              </Grid>
              <Grid container columnSpacing={{ xs: 2, sm: 2, md: 2 }} mt={2}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={regionList}
                    getOptionLabel={(option: any) => option?.region_name}
                    onChange={(_, newValue: any) =>
                      setRegion(newValue?.region_id)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Location"
                        color="secondary"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder="Please select Kinsta region"
                        required
                      />
                    )}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="outlined-multiline-flexible"
                    label="Site Title"
                    placeholder="Please enter Site Title"
                    required
                    fullWidth
                    color="secondary"
                    onChange={(e) => setSiteTitle(e.target.value)}
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
              <Typography
                sx={{ fontSize: "12px", marginTop: "8px", color: "#00000099" }}
              >
                All fields are mandatory.
              </Typography>
            </Box>
            <WebsiteImageOption />
            <Box>
              <Grid container>
                <Grid xs={12}>
                  <Typography sx={{ marginTop: "8px" }}>
                    Connectivity
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <TextField
                    id="outlined-multiline-flexible"
                    label="WP Username"
                    placeholder="email address that will use you for WP login"
                    required
                    fullWidth
                    color="secondary"
                    size="small"
                    margin="dense"
                    value={wpUsername}
                    error={errorWPUserName}
                    onChange={handleEmailChange}
                    helperText={
                      errorWPUserName
                        ? "Must be a valid email address"
                        : "Must be a valid email address"
                    }
                  />
                </Grid>
                <Grid xs={6} sx={{ pl: 2 }}>
                  <FormControl
                    sx={{ mt: 1, mb: 1 }}
                    variant="outlined"
                    fullWidth
                  >
                    <InputLabel
                      htmlFor="outlined-adornment-password"
                      color="secondary"
                      size="small"
                    >
                      WP Password *
                    </InputLabel>
                    <OutlinedInput
                      id="outlined-adornment-password"
                      type={showPassword ? "text" : "password"}
                      size="small"
                      color="secondary"
                      placeholder="Password that will use you for WP login"
                      onChange={handlePasswordChange}
                      value={wpPassword}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword
                                ? "hide the password"
                                : "display the password"
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                      error={error}
                      label="WP Password"
                      required
                    />
                    {error && (
                      <FormHelperText error>
                        Password must be at least 8 characters long.
                      </FormHelperText>
                    )}
                  </FormControl>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      marginTop: "8px",
                      color: "#00000099",
                    }}
                  >
                    A min of 8 characters. We don't store your password
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Stack
              flexDirection={"row"}
              justifyContent={"end"}
              marginTop={2}
              gap={1}
            >
              <Button color="secondary" onClick={handleCancel}>
                CANCEL
              </Button>
              <Button
              data-tracker="55"
                variant="contained"
                color="secondary"
                disabled={
                  !isAllFileldsValid ||
                  isLoading ||
                  error ||
                  !(websiteName.length >= 8)
                }
                onClick={() => {
                  setCreateSite(true);
                  setIsLoading(true);
                }}
              >
                <Stack flexDirection={"row"} gap={1}>
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <AutoFixHighOutlinedIcon />
                  )}
                  Create
                </Stack>
              </Button>
            </Stack>
          </Paper>
        </Dialog>
      )}
    </>
  );
}

export default CreateNewWebsiteModal;
